import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Plus } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, ApiError, jsonBody } from './api'
import { canvasFontSize } from './canvas-font-size'
import { DynamicTimePreview } from './dynamic-time'
import { imageMotionClass, imageMotionStyle } from './image-motion'
import { OverlayLayer, type Overlay } from './overlay-preview'
import { buildPlaylistSequence, type PlaylistItemMode } from './playlist-rules'
import { DynamicTickerBar, type Ticker } from './ticker-preview'
import type { Page } from './types'
import { EmptyState, PageHeader, StatusPill } from './ui'
import { VisualLayoutBuilder, type BuilderZone } from './visual-layout-builder'

export type { Ticker } from './ticker-preview'

type ZoneType = 'IMAGE' | 'VIDEO' | 'TEXT' | 'TICKER' | 'DYNAMIC_TIME'
export type SlideTransition =
  'NONE' | 'FADE' | 'SLIDE_LEFT' | 'SLIDE_RIGHT' | 'ZOOM' | 'WIPE'
type Template =
  | 'FULLSCREEN'
  | 'HORIZONTAL_SPLIT'
  | 'VERTICAL_SPLIT'
  | 'L_BAND'
  | 'FULLSCREEN_BOTTOM_BANNER'
  | 'PRAYER_TIMER'
  | 'CUSTOM'

interface Zone {
  id: string
  name: string
  type: ZoneType
  x: number
  y: number
  width: number
  height: number
  zIndex: number
  style?: Record<string, unknown>
}

export interface Layout {
  id: string
  versionId: string
  name: string
  orientation: 'LANDSCAPE' | 'PORTRAIT'
  template: Template
  version: number
  zones: Zone[]
  createdAt: string
  versions?: {
    id: string
    version: number
    zones: Zone[]
    createdAt: string
  }[]
}

export interface MediaAsset {
  id: string
  name: string
  kind: 'IMAGE' | 'VIDEO'
  status: string
  thumbnailUrl: string | null
}

export interface Playlist {
  id: string
  name: string
  tickerId: string | null
  ticker: Ticker | null
  overlayId: string | null
  overlay: Overlay | null
  leftOverlayId: string | null
  leftOverlay: Overlay | null
  rightOverlayId: string | null
  rightOverlay: Overlay | null
  items: {
    id: string
    slideVersionId: string
    slideName: string
    position: number
    durationMs: number
    transition: SlideTransition
    mode?: PlaylistItemMode
    repeatEvery?: number | null
    activeStartTime?: string | null
    activeEndTime?: string | null
    activeTimezone?: string | null
  }[]
  createdAt: string
  updatedAt: string
}

function previewAnimationClass(
  style: Record<string, unknown> | undefined,
  transition: SlideTransition | undefined
) {
  const animation =
    transition && transition !== 'NONE'
      ? transition
      : typeof style?.entryAnimation === 'string'
        ? style.entryAnimation
        : 'NONE'
  switch (animation) {
    case 'FADE':
    case 'FADE_IN':
      return 'dotit-animate-fade'
    case 'SLIDE_LEFT':
      return 'dotit-animate-slide-left'
    case 'SLIDE_RIGHT':
      return 'dotit-animate-slide-right'
    case 'SLIDE_TOP':
      return 'dotit-animate-slide-top'
    case 'SLIDE_BOTTOM':
      return 'dotit-animate-slide-bottom'
    case 'ZOOM':
    case 'ZOOM_IN':
      return 'dotit-animate-zoom'
    case 'WIPE':
      return 'dotit-animate-wipe'
    default:
      return ''
  }
}

function formattedZoneText(text: string, listStyle: unknown) {
  const marker = {
    BULLET: '•',
    CHECK: '✓',
    ARROW: '→',
    STAR: '★',
  }[typeof listStyle === 'string' ? listStyle : 'NONE']
  if (!marker) return text
  return text
    .split('\n')
    .map((line) => (line.trim() ? `${marker}  ${line}` : ''))
    .join('\n')
}

function SignagePreview({
  layout,
  media,
  transition,
  ticker,
  overlay,
  leftOverlay,
  rightOverlay,
  contentKey,
}: {
  layout: Layout
  media: MediaAsset[]
  transition?: SlideTransition
  ticker?: Ticker | null
  overlay?: Overlay | null
  leftOverlay?: Overlay | null
  rightOverlay?: Overlay | null
  contentKey?: string
}) {
  return (
    <div
      className={`mx-auto overflow-hidden rounded-lg border bg-zinc-950 ${
        layout.orientation === 'PORTRAIT'
          ? 'aspect-[9/16] max-h-[70vh]'
          : 'aspect-video w-full'
      }`}
      style={{ containerType: 'inline-size' }}
    >
      <div className='relative size-full'>
        <div
          key={contentKey}
          data-slide-transition-layer
          className={`absolute inset-0 overflow-hidden ${previewAnimationClass(undefined, transition)}`}
        >
          {layout.zones.map((zone) => {
            const assetId =
              typeof zone.style?.assetId === 'string'
                ? zone.style.assetId
                : undefined
            const asset = media.find((candidate) => candidate.id === assetId)
            const text =
              typeof zone.style?.text === 'string' ? zone.style.text : zone.name
            const color =
              typeof zone.style?.color === 'string'
                ? zone.style.color
                : '#ffffff'
            const background =
              typeof zone.style?.background === 'string'
                ? zone.style.background
                : '#18181b'
            const backgroundTransparent =
              zone.style?.backgroundTransparent === true
            const fontSize =
              typeof zone.style?.fontSize === 'number'
                ? zone.style.fontSize
                : 32
            const lineHeight =
              typeof zone.style?.lineHeight === 'number'
                ? zone.style.lineHeight
                : 1.2
            const fontWeight =
              typeof zone.style?.fontWeight === 'number'
                ? zone.style.fontWeight
                : 700
            const fontFamily =
              typeof zone.style?.fontFamily === 'string'
                ? zone.style.fontFamily
                : 'Hind Siliguri'
            const textAlign =
              typeof zone.style?.textAlign === 'string'
                ? zone.style.textAlign
                : 'CENTER'
            const verticalAlign =
              typeof zone.style?.verticalAlign === 'string'
                ? zone.style.verticalAlign
                : 'CENTER'
            const padding = backgroundTransparent
              ? 0
              : typeof zone.style?.padding === 'number'
                ? zone.style.padding
                : 16
            const animationDurationMs =
              typeof zone.style?.animationDurationMs === 'number'
                ? zone.style.animationDurationMs
                : 650
            const objectFit =
              typeof zone.style?.objectFit === 'string'
                ? zone.style.objectFit.toLowerCase()
                : 'cover'
            const tickerSpeed =
              typeof zone.style?.tickerSpeed === 'number'
                ? zone.style.tickerSpeed
                : 70
            return (
              <div
                key={zone.id}
                className={`absolute overflow-hidden border border-white/10 ${previewAnimationClass(
                  zone.style,
                  undefined
                )}`}
                style={{
                  left: `${zone.x * 100}%`,
                  top: `${zone.y * 100}%`,
                  width: `${zone.width * 100}%`,
                  height: `${zone.height * 100}%`,
                  zIndex: zone.zIndex,
                  backgroundColor: backgroundTransparent
                    ? 'transparent'
                    : background,
                  color,
                  animationDuration: `${animationDurationMs}ms`,
                  animationDelay: `${
                    typeof zone.style?.entryDelayMs === 'number'
                      ? zone.style.entryDelayMs
                      : 0
                  }ms`,
                }}
              >
                {asset?.thumbnailUrl ? (
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.name}
                    className={`size-full ${imageMotionClass(zone.style)}`}
                    style={{
                      objectFit: ['contain', 'fill'].includes(objectFit)
                        ? (objectFit as 'contain' | 'fill')
                        : 'cover',
                      ...imageMotionStyle(zone.style),
                    }}
                  />
                ) : zone.type === 'TEXT' ||
                  zone.type === 'TICKER' ||
                  zone.type === 'DYNAMIC_TIME' ? (
                  <div
                    className={`flex size-full ${
                      zone.type === 'TICKER' ? 'whitespace-nowrap' : ''
                    }`}
                    style={{
                      alignItems:
                        verticalAlign === 'TOP'
                          ? 'flex-start'
                          : verticalAlign === 'BOTTOM'
                            ? 'flex-end'
                            : 'center',
                      justifyContent:
                        textAlign === 'LEFT'
                          ? 'flex-start'
                          : textAlign === 'RIGHT'
                            ? 'flex-end'
                            : 'center',
                      textAlign: textAlign.toLowerCase() as
                        'left' | 'center' | 'right',
                      fontSize: canvasFontSize(fontSize),
                      fontWeight,
                      lineHeight,
                      fontFamily,
                      padding: `${padding}px`,
                      whiteSpace:
                        zone.type === 'TICKER' ? 'nowrap' : 'pre-wrap',
                    }}
                  >
                    <span
                      className={
                        zone.type === 'TICKER' ? 'dotit-ticker-preview' : ''
                      }
                      style={
                        zone.type === 'TICKER'
                          ? {
                              animationDuration: `${Math.max(
                                3,
                                Math.min(18, 120 / tickerSpeed)
                              )}s`,
                            }
                          : undefined
                      }
                    >
                      {zone.type === 'DYNAMIC_TIME' ? (
                        <DynamicTimePreview style={zone.style} />
                      ) : (
                        formattedZoneText(text, zone.style?.listStyle)
                      )}
                    </span>
                  </div>
                ) : (
                  <div className='grid size-full place-items-center text-xs text-zinc-500'>
                    {zone.name} · {zone.type}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {ticker ? (
          <DynamicTickerBar
            ticker={ticker}
            overlay
            leftOverlay={leftOverlay}
            rightOverlay={rightOverlay}
          />
        ) : null}
        <OverlayLayer overlay={overlay} />
      </div>
    </div>
  )
}

export function PlaylistPreview({
  playlist,
  slides,
  media,
}: {
  playlist: Playlist
  slides: Layout[]
  media: MediaAsset[]
}) {
  const [index, setIndex] = useState(0)
  const [now, setNow] = useState(() => new Date())
  const sequence = buildPlaylistSequence(playlist.items, now)
  const item = sequence[index % Math.max(sequence.length, 1)]
  const slide = slides.find(
    (candidate) => candidate.versionId === item?.slideVersionId
  )
  useEffect(() => {
    setIndex(0)
  }, [playlist.id])
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15_000)
    return () => window.clearInterval(timer)
  }, [])
  useEffect(() => {
    if (!item || sequence.length < 2) return
    const timer = window.setTimeout(
      () => setIndex((current) => (current + 1) % sequence.length),
      Math.max(1000, item.durationMs)
    )
    return () => window.clearTimeout(timer)
  }, [item, sequence.length])
  return slide && item ? (
    <SignagePreview
      layout={slide}
      media={media}
      transition={item.transition}
      contentKey={`${playlist.id}-${item.id}-${index}`}
      ticker={playlist.ticker}
      overlay={playlist.overlay}
      leftOverlay={playlist.leftOverlay}
      rightOverlay={playlist.rightOverlay}
    />
  ) : (
    <div className='grid aspect-video place-items-center rounded-lg border bg-zinc-950 text-sm text-zinc-400'>
      Add a slide to preview this playlist.
    </div>
  )
}
const templates: Record<Template, Omit<Zone, 'id' | 'type'>[]> = {
  FULLSCREEN: [{ name: 'Main', x: 0, y: 0, width: 1, height: 1, zIndex: 0 }],
  HORIZONTAL_SPLIT: [
    { name: 'Top', x: 0, y: 0, width: 1, height: 0.5, zIndex: 0 },
    { name: 'Bottom', x: 0, y: 0.5, width: 1, height: 0.5, zIndex: 1 },
  ],
  VERTICAL_SPLIT: [
    { name: 'Left', x: 0, y: 0, width: 0.5, height: 1, zIndex: 0 },
    { name: 'Right', x: 0.5, y: 0, width: 0.5, height: 1, zIndex: 1 },
  ],
  L_BAND: [
    { name: 'Main', x: 0, y: 0, width: 0.75, height: 0.75, zIndex: 0 },
    { name: 'Side', x: 0.75, y: 0, width: 0.25, height: 1, zIndex: 1 },
    { name: 'Bottom', x: 0, y: 0.75, width: 0.75, height: 0.25, zIndex: 2 },
  ],
  FULLSCREEN_BOTTOM_BANNER: [
    { name: 'Main', x: 0, y: 0, width: 1, height: 0.82, zIndex: 0 },
    { name: 'Banner', x: 0, y: 0.82, width: 1, height: 0.18, zIndex: 1 },
  ],
  PRAYER_TIMER: [
    { name: 'Background', x: 0, y: 0, width: 1, height: 1, zIndex: 0 },
    { name: 'Headline', x: 0.08, y: 0.08, width: 0.84, height: 0.2, zIndex: 1 },
    {
      name: 'Timer label',
      x: 0.18,
      y: 0.31,
      width: 0.64,
      height: 0.11,
      zIndex: 2,
    },
    {
      name: 'Prayer timer',
      x: 0.14,
      y: 0.43,
      width: 0.72,
      height: 0.31,
      zIndex: 3,
    },
    { name: 'Footer', x: 0.08, y: 0.78, width: 0.84, height: 0.14, zIndex: 4 },
  ],
  CUSTOM: [],
}

function baseZoneStyle(type: ZoneType) {
  return {
    text:
      type === 'TICKER'
        ? 'Breaking news and announcements move here'
        : type === 'TEXT'
          ? 'Add your message'
          : '',
    color: '#ffffff',
    background:
      type === 'TEXT' || type === 'TICKER'
        ? '#dc2626'
        : type === 'DYNAMIC_TIME'
          ? '#003f39'
          : '#18181b',
    fontSize: type === 'DYNAMIC_TIME' ? 96 : type === 'TICKER' ? 28 : 42,
    lineHeight: 1.2,
    fontWeight: 700,
    fontFamily: 'Hind Siliguri',
    textAlign: 'CENTER',
    objectFit: 'COVER',
    entryAnimation: 'FADE_IN',
    entryDelayMs: 0,
    tickerSpeed: 70,
    timerMode: 'COUNTDOWN',
    targetTime: '13:15',
    timezone: 'Asia/Dhaka',
    locale: 'bn-BD',
    hourCycle: 'H12',
    showSeconds: true,
    imageMotion: 'NONE',
    motionDurationMs: 12_000,
    motionIntensity: 5,
    motionDirection: 'LEFT_RIGHT',
    motionFocalPoint: 'CENTER',
    motionLoop: true,
  }
}

function layoutZones(
  template: Template,
  types: ZoneType[],
  existingZones: Zone[] = []
): BuilderZone[] {
  return templates[template].map((zone, index) => {
    const type =
      existingZones[index]?.type ??
      types[index] ??
      (index === 0 ? 'VIDEO' : 'TEXT')
    return {
      ...zone,
      id: existingZones[index]?.id ?? crypto.randomUUID(),
      name: existingZones[index]?.name ?? zone.name,
      type,
      style: existingZones[index]?.style ?? baseZoneStyle(type),
    }
  })
}

function customStarterZones(): BuilderZone[] {
  return [
    {
      id: crypto.randomUUID(),
      name: 'Hero image',
      type: 'IMAGE',
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      zIndex: 0,
      style: baseZoneStyle('IMAGE'),
    },
    {
      id: crypto.randomUUID(),
      name: 'Headline',
      type: 'TEXT',
      x: 0.08,
      y: 0.1,
      width: 0.55,
      height: 0.24,
      zIndex: 1,
      style: {
        ...baseZoneStyle('TEXT'),
        text: 'Your headline goes here',
        background: '#dc2626cc',
        fontSize: 52,
      },
    },
    {
      id: crypto.randomUUID(),
      name: 'News ticker',
      type: 'TICKER',
      x: 0,
      y: 0.86,
      width: 1,
      height: 0.14,
      zIndex: 2,
      style: baseZoneStyle('TICKER'),
    },
  ]
}

function prayerTimerStarterZones(): BuilderZone[] {
  const zones = layoutZones('PRAYER_TIMER', [
    'TEXT',
    'TEXT',
    'TEXT',
    'DYNAMIC_TIME',
    'TEXT',
  ])
  const copy = [
    '',
    'মসজিদে নূর জামে মসজিদ',
    'নামাজের সময় বাকি',
    '',
    'নামাজের সময় ১:১৫-এ',
  ]
  return zones.map((zone, index) => ({
    ...zone,
    style: {
      ...zone.style,
      text: copy[index],
      background: '#003f39',
      color: index === 1 || index === 3 ? '#f7d36d' : '#ffffff',
      fontSize: index === 1 ? 58 : index === 3 ? 118 : index === 2 ? 36 : 34,
      fontWeight: index === 1 || index === 3 ? 900 : 700,
      entryDelayMs: index * 150,
      timerMode: 'COUNTDOWN',
      targetTime: '13:15',
      timezone: 'Asia/Dhaka',
      locale: 'bn-BD',
      hourCycle: 'H12',
      showSeconds: true,
    },
  }))
}

function starterZonesForTemplate(template: Template) {
  if (template === 'CUSTOM') return customStarterZones()
  if (template === 'PRAYER_TIMER') return prayerTimerStarterZones()
  return layoutZones(
    template,
    templates[template].map((_, index) => (index === 0 ? 'IMAGE' : 'TEXT'))
  )
}
// eslint-disable-next-line react-refresh/only-export-components
export function flattenLayoutVersions(layouts: Layout[] | undefined) {
  return (
    layouts?.flatMap((layout) =>
      layout.versions?.length
        ? layout.versions.map((version) => ({
            ...layout,
            versionId: version.id,
            version: version.version,
            zones: version.zones,
            createdAt: version.createdAt,
          }))
        : [layout]
    ) ?? []
  )
}

export function LayoutEditorPage() {
  const navigate = useNavigate()
  const { layoutId } = useParams<{ layoutId: string }>()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [orientation, setOrientation] = useState<'LANDSCAPE' | 'PORTRAIT'>(
    'LANDSCAPE'
  )
  const [template, setTemplate] = useState<Template>('CUSTOM')
  const [draftZones, setDraftZones] = useState<BuilderZone[]>(() =>
    customStarterZones()
  )
  const [initializedLayoutId, setInitializedLayoutId] = useState<string | null>(
    null
  )
  const layouts = useQuery({
    queryKey: ['layouts'],
    queryFn: () => api<Layout[]>('/layouts'),
    refetchOnMount: 'always',
  })
  const layoutMedia = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
  })
  const editingLayout = layoutId
    ? layouts.data?.find((layout) => layout.id === layoutId)
    : undefined

  useEffect(() => {
    if (!editingLayout || initializedLayoutId === editingLayout.id) return
    setName(editingLayout.name)
    setOrientation(editingLayout.orientation)
    setTemplate(editingLayout.template)
    setDraftZones(editingLayout.zones.map((zone) => ({ ...zone })))
    setInitializedLayoutId(editingLayout.id)
  }, [editingLayout, initializedLayoutId])

  const saveLayout = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/layouts/${id}` : '/layouts', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['layouts'] })
      toast.success(
        variables.id
          ? 'New immutable layout version saved.'
          : 'Visual slide created.'
      )
      navigate('/app/slides')
    },
    onError: (error) => toast.error(error.message),
  })
  const slideNameError =
    saveLayout.error instanceof ApiError
      ? saveLayout.error.fieldErrors?.name?.[0]
      : undefined

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    saveLayout.mutate({
      id: editingLayout?.id,
      body: { name, orientation, template, zones: draftZones },
    })
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='Content / Slides'
        title={editingLayout ? 'Edit visual slide' : 'Create a visual slide'}
        description={
          editingLayout
            ? `Saving creates version ${editingLayout.version + 1}; existing publications can continue using version ${editingLayout.version}.`
            : 'Design on a full-page canvas with grouped layer, typography, and animation controls.'
        }
        action={
          <Button variant='outline' onClick={() => navigate('/app/slides')}>
            <ArrowLeft className='size-4' />
            Back to slides
          </Button>
        }
      />

      {layoutId && !editingLayout && layouts.isLoading ? (
        <p className='rounded-xl border bg-muted/20 p-6 text-sm text-muted-foreground'>
          Loading slide editor…
        </p>
      ) : layoutId && !editingLayout ? (
        <EmptyState
          title='Slide not found'
          description='The requested slide is unavailable or has been removed.'
          action={
            <Button onClick={() => navigate('/app/slides')}>
              Back to slides
            </Button>
          }
        />
      ) : (
        <form className='space-y-5' onSubmit={submit}>
          <section className='grid gap-4 rounded-xl border bg-card p-4 sm:grid-cols-3'>
            <div className='space-y-2'>
              <Label htmlFor='layout-name'>Slide name</Label>
              <Input
                id='layout-name'
                value={name}
                placeholder='Summer offer slide'
                required
                aria-invalid={Boolean(slideNameError)}
                aria-describedby={
                  slideNameError ? 'layout-name-error' : undefined
                }
                onChange={(event) => {
                  setName(event.target.value)
                  if (saveLayout.error) saveLayout.reset()
                }}
              />
              {slideNameError ? (
                <p id='layout-name-error' className='text-xs text-destructive'>
                  {slideNameError}
                </p>
              ) : null}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='orientation'>Orientation</Label>
              <select
                id='orientation'
                className='h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60'
                value={orientation}
                disabled={Boolean(editingLayout)}
                onChange={(event) =>
                  setOrientation(event.target.value as 'LANDSCAPE' | 'PORTRAIT')
                }
              >
                <option value='LANDSCAPE'>Landscape 16:9</option>
                <option value='PORTRAIT'>Portrait 9:16</option>
              </select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='template'>Canvas type</Label>
              <select
                id='template'
                className='h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60'
                value={template}
                disabled={Boolean(editingLayout)}
                onChange={(event) => {
                  const value = event.target.value as Template
                  setTemplate(value)
                  setDraftZones(starterZonesForTemplate(value))
                }}
              >
                {Object.keys(templates).map((value) => (
                  <option key={value} value={value}>
                    {value === 'CUSTOM'
                      ? 'Custom canvas · drag & resize'
                      : value === 'PRAYER_TIMER'
                        ? 'Prayer / event timer'
                        : value.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <VisualLayoutBuilder
            orientation={orientation}
            zones={draftZones}
            onChange={setDraftZones}
            freeform={template === 'CUSTOM' || template === 'PRAYER_TIMER'}
            media={
              layoutMedia.data?.items.filter(
                (asset) => asset.status === 'READY'
              ) ?? []
            }
          />

          <section className='grid gap-3 rounded-xl border bg-muted/20 p-4 md:grid-cols-2'>
            <div className='rounded-lg border bg-background p-3'>
              <p className='text-sm font-semibold'>
                Layer motion inside a slide
              </p>
              <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
                Select a layer, then set its entry animation, duration, and
                start delay. Use increasing delays such as 0ms, 300ms, and 600ms
                to reveal layers in sequence.
              </p>
            </div>
            <div className='rounded-lg border bg-background p-3'>
              <p className='text-sm font-semibold'>Transition between slides</p>
              <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
                Add this slide to a Playlist, then choose Cut, Fade, Slide left,
                Slide right, Zoom, or Wipe on the playlist item. That transition
                moves the complete outgoing slide to the next slide.
              </p>
            </div>
          </section>

          <div className='sticky bottom-3 z-20 flex items-center justify-between gap-4 rounded-xl border bg-background/95 p-3 shadow-lg backdrop-blur'>
            <p className='text-xs text-muted-foreground'>
              {draftZones.length}/20 layers · one video layer maximum · changes
              are saved as an immutable version
            </p>
            <Button disabled={saveLayout.isPending || !draftZones.length}>
              {saveLayout.isPending
                ? 'Saving…'
                : editingLayout
                  ? 'Save as new version'
                  : 'Create visual slide'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

export function LayoutsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingLayoutId, setEditingLayoutId] = useState<string | null>(null)
  const [orientation, setOrientation] = useState<'LANDSCAPE' | 'PORTRAIT'>(
    'LANDSCAPE'
  )
  const [template, setTemplate] = useState<Template>('CUSTOM')
  const [draftZones, setDraftZones] = useState<BuilderZone[]>(() =>
    customStarterZones()
  )
  const [previewLayoutId, setPreviewLayoutId] = useState<string | null>(null)
  const layouts = useQuery({
    queryKey: ['layouts'],
    queryFn: () => api<Layout[]>('/layouts'),
    refetchOnMount: 'always',
  })
  const layoutMedia = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
  })
  const editingLayout = layouts.data?.find(
    (layout) => layout.id === editingLayoutId
  )
  const saveLayout = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/layouts/${id}` : '/layouts', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['layouts'] })
      setOpen(false)
      setEditingLayoutId(null)
      toast.success(
        variables.id
          ? 'New immutable layout version saved.'
          : 'Visual layout created.'
      )
    },
    onError: (error) => toast.error(error.message),
  })
  const startNewLayout = () => navigate('/app/slides/new')
  const startEditingLayout = (layout: Layout) =>
    navigate(`/app/slides/${layout.id}/edit`)
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    saveLayout.mutate({
      id: editingLayout?.id,
      body: {
        name: data.get('name'),
        orientation,
        template,
        zones: draftZones,
      },
    })
  }
  const previewLayout = layouts.data?.find(
    (layout) => layout.id === previewLayoutId
  )
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Visual slide builder'
        description='Build complete reusable slides with assigned images, videos, text, colors, and layer animations. Playlists only control slide order, timing, transitions, and ticker selection.'
        action={
          <Dialog
            open={open}
            onOpenChange={(isOpen) => {
              setOpen(isOpen)
              if (!isOpen) setEditingLayoutId(null)
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={startNewLayout}>
                <Plus className='size-4' />
                New slide
              </Button>
            </DialogTrigger>
            <DialogContent className='max-h-[94vh] overflow-y-auto sm:max-w-6xl'>
              <DialogHeader>
                <DialogTitle>
                  {editingLayout
                    ? 'Edit visual slide'
                    : 'Create a visual slide'}
                </DialogTitle>
                <DialogDescription>
                  {editingLayout
                    ? `Saving creates version ${editingLayout.version + 1}; published playlists can continue using version ${editingLayout.version}.`
                    : 'Start with a freeform canvas or a production-safe fixed template.'}
                </DialogDescription>
              </DialogHeader>
              <form
                key={editingLayout?.id ?? 'new-layout'}
                className='space-y-5'
                onSubmit={submit}
              >
                <div className='grid gap-4 sm:grid-cols-3'>
                  <div className='space-y-2'>
                    <Label htmlFor='layout-name'>Slide name</Label>
                    <Input
                      id='layout-name'
                      name='name'
                      defaultValue={editingLayout?.name ?? ''}
                      placeholder='Summer offer slide'
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='orientation'>Orientation</Label>
                    <select
                      id='orientation'
                      name='orientation'
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60'
                      value={orientation}
                      disabled={Boolean(editingLayout)}
                      onChange={(event) =>
                        setOrientation(
                          event.target.value as 'LANDSCAPE' | 'PORTRAIT'
                        )
                      }
                    >
                      <option value='LANDSCAPE'>Landscape 16:9</option>
                      <option value='PORTRAIT'>Portrait 9:16</option>
                    </select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='template'>Canvas type</Label>
                    <select
                      id='template'
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60'
                      value={template}
                      disabled={Boolean(editingLayout)}
                      onChange={(event) => {
                        const value = event.target.value as Template
                        setTemplate(value)
                        setDraftZones(starterZonesForTemplate(value))
                      }}
                    >
                      {Object.keys(templates).map((value) => (
                        <option key={value} value={value}>
                          {value === 'CUSTOM'
                            ? 'Custom canvas · drag & resize'
                            : value === 'PRAYER_TIMER'
                              ? 'Prayer / event timer'
                              : value.replaceAll('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {editingLayout ? (
                  <p className='rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground'>
                    Orientation and canvas type remain fixed across versions.
                    Layer positions, sizes, content types, colors, text, and
                    animations can be edited.
                  </p>
                ) : null}

                <VisualLayoutBuilder
                  orientation={orientation}
                  zones={draftZones}
                  onChange={setDraftZones}
                  freeform={
                    template === 'CUSTOM' || template === 'PRAYER_TIMER'
                  }
                  media={
                    layoutMedia.data?.items.filter(
                      (asset) => asset.status === 'READY'
                    ) ?? []
                  }
                />

                <div className='flex items-center justify-between gap-4 rounded-lg border bg-muted/20 p-3'>
                  <p className='text-xs text-muted-foreground'>
                    {draftZones.length}/20 layers · one video layer maximum ·
                    normalized geometry works at any screen resolution
                  </p>
                  <Button disabled={saveLayout.isPending || !draftZones.length}>
                    {saveLayout.isPending
                      ? 'Saving…'
                      : editingLayout
                        ? 'Save as new version'
                        : 'Create visual slide'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <Dialog
        open={Boolean(previewLayout)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreviewLayoutId(null)
        }}
      >
        <DialogContent className='sm:max-w-5xl'>
          <DialogHeader>
            <DialogTitle>{previewLayout?.name ?? 'Slide preview'}</DialogTitle>
            <DialogDescription>
              Complete slide preview with its saved media, text, and styling.
            </DialogDescription>
          </DialogHeader>
          {previewLayout ? (
            <SignagePreview
              layout={previewLayout}
              media={layoutMedia.data?.items ?? []}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      {layouts.data?.length ? (
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
          {layouts.data.map((layout) => {
            return (
              <article
                key={layout.id}
                className='rounded-xl border bg-card p-4'
              >
                <SignagePreview
                  layout={layout}
                  media={layoutMedia.data?.items ?? []}
                />
                <div className='mt-4 flex items-start justify-between gap-3'>
                  <div>
                    <h2 className='font-semibold'>{layout.name}</h2>
                    <p className='mt-1 text-xs text-muted-foreground'>
                      {layout.template === 'CUSTOM'
                        ? 'CUSTOM CANVAS'
                        : layout.template.replaceAll('_', ' ')}{' '}
                      · {layout.orientation.toLowerCase()} ·{' '}
                      {layout.zones.length} layers
                    </p>
                  </div>
                  <StatusPill value={`V${layout.version}`} tone='neutral' />
                </div>
                <div className='mt-4 grid grid-cols-2 gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setPreviewLayoutId(layout.id)}
                  >
                    View preview
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => startEditingLayout(layout)}
                  >
                    <Pencil className='size-4' />
                    Edit builder
                  </Button>
                </div>
              </article>
            )
          })}
        </div>
      ) : (
        <EmptyState
          title='No visual slides yet'
          description='Start with a freeform drag-and-drop canvas or a fixed signage template.'
          action={<Button onClick={startNewLayout}>Create visual slide</Button>}
        />
      )}
    </div>
  )
}
/* Legacy zone-to-media playlist editor retained in source history only.
export function PlaylistsPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingPlaylistId, setEditingPlaylistId] = useState<string | null>(
    null
  )
  const [layoutId, setLayoutId] = useState('')
  const [previewPlaylistId, setPreviewPlaylistId] = useState<string | null>(
    null
  )
  const [items, setItems] = useState<PlaylistEditorItem[]>([
    { zoneId: '', assetId: '', durationMs: 10000, transition: 'FADE' },
  ])
  const playlists = useQuery({
    queryKey: ['playlists'],
    queryFn: () => api<Playlist[]>('/playlists'),
    refetchOnMount: 'always',
  })
  const layouts = useQuery({
    queryKey: ['layouts'],
    queryFn: () => api<Layout[]>('/layouts'),
  })
  const media = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
  })
  const layoutVersions = useMemo(
    () => flattenLayoutVersions(layouts.data),
    [layouts.data]
  )
  const editingPlaylist = playlists.data?.find(
    (playlist) => playlist.id === editingPlaylistId
  )
  const selectedLayout = layoutVersions.find(
    (layout) => layout.versionId === layoutId
  )
  const savePlaylist = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/playlists/${id}` : '/playlists', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['playlists'] })
      setOpen(false)
      setEditingPlaylistId(null)
      toast.success(variables.id ? 'Playlist updated.' : 'Playlist created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const startNewPlaylist = () => {
    setEditingPlaylistId(null)
    setLayoutId('')
    setItems([
      { zoneId: '', assetId: '', durationMs: 10000, transition: 'FADE' },
    ])
    setOpen(true)
  }
  const startEditingPlaylist = (playlist: Playlist) => {
    setEditingPlaylistId(playlist.id)
    setLayoutId(playlist.layoutVersionId)
    setItems(
      playlist.items.map((item) => ({
        zoneId: item.zoneId,
        assetId: item.assetId,
        durationMs: item.durationMs,
        transition: item.transition ?? 'NONE',
      }))
    )
    setOpen(true)
  }
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    savePlaylist.mutate({
      id: editingPlaylist?.id,
      body: {
        name: data.get('name'),
        layoutVersionId: layoutId,
        items: items.map((item, position) => ({
          ...item,
          position,
        })),
      },
    })
  }
  const readyMedia = useMemo(
    () => media.data?.items.filter((asset) => asset.status === 'READY') ?? [],
    [media.data]
  )
  const previewPlaylist = playlists.data?.find(
    (playlist) => playlist.id === previewPlaylistId
  )
  const previewPlaylistLayout = layoutVersions.find(
    (layout) => layout.versionId === previewPlaylist?.layoutVersionId
  )
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Reusable playlists'
        description='Build ordered, reusable sequences against one immutable layout version. Assets stay referenced by checksum-ready variants.'
        action={
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => void playlists.refetch()}
              disabled={playlists.isFetching}
            >
              <RefreshCw
                className={
                  playlists.isFetching ? 'size-4 animate-spin' : 'size-4'
                }
              />
              Refresh
            </Button>
            <Dialog
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen)
                if (!isOpen) setEditingPlaylistId(null)
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={startNewPlaylist}
                >
                  <Plus className='size-4' />
                  New playlist
                </Button>
              </DialogTrigger>
              <DialogContent className='sm:max-w-2xl'>
                <DialogHeader>
                  <DialogTitle>
                    {editingPlaylist ? 'Edit playlist' : 'Create a playlist'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingPlaylist
                      ? 'Update the layout version, media order, and playback duration.'
                      : 'Add an ordered item for each image or video zone.'}
                  </DialogDescription>
                </DialogHeader>
                <form
                  key={editingPlaylist?.id ?? 'new-playlist'}
                  className='space-y-4'
                  onSubmit={submit}
                >
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='playlist-name'>Name</Label>
                      <Input
                        id='playlist-name'
                        name='name'
                        defaultValue={editingPlaylist?.name ?? ''}
                        required
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='playlist-layout'>Layout version</Label>
                      <select
                        id='playlist-layout'
                        className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                        value={layoutId}
                        required
                        onChange={(event) => {
                          setLayoutId(event.target.value)
                          setItems([
                            {
                              zoneId: '',
                              assetId: '',
                              durationMs: 10000,
                              transition: 'FADE',
                            },
                          ])
                        }}
                      >
                        <option value=''>Select a layout</option>
                        {layoutVersions.map((layout) => (
                          <option
                            key={layout.versionId}
                            value={layout.versionId}
                          >
                            {layout.name} · v{layout.version}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className='space-y-3'>
                    {items.map((item, index) => (
                      <div
                        key={index}
                        className='grid gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_1fr_110px_150px_auto]'
                      >
                        <select
                          aria-label={`Zone ${index + 1}`}
                          className='h-9 rounded-md border bg-background px-2 text-sm'
                          value={item.zoneId}
                          onChange={(event) =>
                            setItems((current) =>
                              current.map((candidate, itemIndex) =>
                                itemIndex === index
                                  ? { ...candidate, zoneId: event.target.value }
                                  : candidate
                              )
                            )
                          }
                        >
                          <option value=''>Select zone</option>
                          {selectedLayout?.zones
                            .filter((zone) =>
                              ['IMAGE', 'VIDEO'].includes(zone.type)
                            )
                            .map((zone) => (
                              <option key={zone.id} value={zone.id}>
                                {zone.name} · {zone.type}
                              </option>
                            ))}
                        </select>
                        <select
                          aria-label={`Media ${index + 1}`}
                          className='h-9 rounded-md border bg-background px-2 text-sm'
                          value={item.assetId}
                          onChange={(event) =>
                            setItems((current) =>
                              current.map((candidate, itemIndex) =>
                                itemIndex === index
                                  ? {
                                      ...candidate,
                                      assetId: event.target.value,
                                    }
                                  : candidate
                              )
                            )
                          }
                        >
                          <option value=''>Select media</option>
                          {readyMedia
                            .filter((asset) => {
                              const zone = selectedLayout?.zones.find(
                                (candidate) => candidate.id === item.zoneId
                              )
                              return !zone || zone.type === asset.kind
                            })
                            .map((asset) => (
                              <option key={asset.id} value={asset.id}>
                                {asset.name}
                              </option>
                            ))}
                        </select>
                        <Input
                          aria-label={`Duration ${index + 1}`}
                          type='number'
                          min={1000}
                          step={1000}
                          value={item.durationMs}
                          onChange={(event) =>
                            setItems((current) =>
                              current.map((candidate, itemIndex) =>
                                itemIndex === index
                                  ? {
                                      ...candidate,
                                      durationMs: Number(event.target.value),
                                    }
                                  : candidate
                              )
                            )
                          }
                        />
                        <select
                          aria-label={`Transition ${index + 1}`}
                          className='h-9 rounded-md border bg-background px-2 text-sm'
                          value={item.transition}
                          onChange={(event) =>
                            setItems((current) =>
                              current.map((candidate, itemIndex) =>
                                itemIndex === index
                                  ? {
                                      ...candidate,
                                      transition: event.target
                                        .value as SlideTransition,
                                    }
                                  : candidate
                              )
                            )
                          }
                        >
                          <option value='NONE'>Cut</option>
                          <option value='FADE'>Fade</option>
                          <option value='SLIDE_LEFT'>Slide left</option>
                          <option value='SLIDE_RIGHT'>Slide right</option>
                          <option value='ZOOM'>Zoom</option>
                          <option value='WIPE'>Wipe</option>
                        </select>
                        <Button
                          type='button'
                          size='icon'
                          variant='ghost'
                          disabled={items.length === 1}
                          onClick={() =>
                            setItems((current) =>
                              current.filter(
                                (_, itemIndex) => itemIndex !== index
                              )
                            )
                          }
                          aria-label={`Remove item ${index + 1}`}
                        >
                          <Trash2 className='size-4' />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        setItems((current) => [
                          ...current,
                          {
                            zoneId: '',
                            assetId: '',
                            durationMs: 10000,
                            transition: 'FADE',
                          },
                        ])
                      }
                    >
                      <Plus className='size-4' />
                      Add item
                    </Button>
                  </div>
                  <Button
                    className='w-full'
                    disabled={
                      savePlaylist.isPending ||
                      !layoutId ||
                      items.some((item) => !item.zoneId || !item.assetId)
                    }
                  >
                    {savePlaylist.isPending
                      ? 'Saving…'
                      : editingPlaylist
                        ? 'Save playlist changes'
                        : 'Create playlist'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <Dialog
        open={Boolean(previewPlaylist && previewPlaylistLayout)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreviewPlaylistId(null)
        }}
      >
        <DialogContent className='sm:max-w-5xl'>
          <DialogHeader>
            <DialogTitle>
              {previewPlaylist?.name ?? 'Playlist preview'}
            </DialogTitle>
            <DialogDescription>
              {previewPlaylistLayout?.name ?? 'Loading layout'} ·{' '}
              {previewPlaylist?.items.length ?? 0} item(s)
            </DialogDescription>
          </DialogHeader>
          {previewPlaylist && previewPlaylistLayout ? (
            <SignagePreview
              layout={previewPlaylistLayout}
              playlist={previewPlaylist}
              media={media.data?.items ?? []}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      {playlists.isError ? (
        <EmptyState
          title='Could not load playlists'
          description={playlists.error.message}
          action={
            <Button onClick={() => void playlists.refetch()}>
              <RefreshCw className='size-4' />
              Try again
            </Button>
          }
        />
      ) : playlists.data?.length ? (
        <TableShell
          headers={[
            'Preview',
            'Playlist',
            'Layout version',
            'Items',
            'Updated',
            'Actions',
          ]}
        >
          {playlists.data.map((playlist) => {
            const playlistAssets = playlist.items
              .map((item) =>
                media.data?.items.find((asset) => asset.id === item.assetId)
              )
              .filter((asset): asset is MediaAsset => Boolean(asset))
            const previewAsset = playlistAssets[0]
            const playlistLayout = layoutVersions.find(
              (layout) => layout.versionId === playlist.layoutVersionId
            )
            return (
              <tr key={playlist.id}>
                <td className='px-4 py-3'>
                  {previewAsset?.thumbnailUrl ? (
                    <img
                      src={previewAsset.thumbnailUrl}
                      alt={previewAsset.name}
                      className='h-12 w-20 rounded-md border object-cover'
                    />
                  ) : (
                    <div className='grid h-12 w-20 place-items-center rounded-md border bg-muted text-xs text-muted-foreground'>
                      No preview
                    </div>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <div className='font-semibold'>{playlist.name}</div>
                  {playlistAssets.length ? (
                    <div className='mt-1 text-xs text-muted-foreground'>
                      {playlistAssets.map((asset) => asset.name).join(', ')}
                    </div>
                  ) : null}
                </td>
                <td className='px-4 py-3 text-sm text-muted-foreground'>
                  {playlistLayout
                    ? `${playlistLayout.name} · v${playlistLayout.version}`
                    : playlist.layoutVersionId.slice(0, 8)}
                </td>
                <td className='px-4 py-3'>{playlist.items.length}</td>
                <td className='px-4 py-3 text-muted-foreground'>
                  {new Date(playlist.updatedAt).toLocaleString()}
                </td>
                <td className='px-4 py-3'>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setPreviewPlaylistId(playlist.id)}
                    >
                      View preview
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => startEditingPlaylist(playlist)}
                    >
                      <Pencil className='size-4' />
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            )
          })}
        </TableShell>
      ) : (
        <EmptyState
          title='No playlists yet'
          description='Create a layout and process media, then assemble the first reusable playlist.'
          action={
            <Button
              onClick={startNewPlaylist}
              disabled={!layoutVersions.length || !readyMedia.length}
            >
              Create playlist
            </Button>
          }
        />
      )}
    </div>
  )
}
*/
